import { Ref, useImperativeHandle } from "react";
import {
  CreatorFragmentCommonProps,
  CreatorFragmentHandle,
} from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CreatorFragment";
import { getByPath } from "@coworker/apprestructured/src/shared/helpers/object.helper";
import { isLoading } from "@coworker/apprestructured/src/task-management/loading";
import { useTranslation } from "@coworker/locales";
import {
  FieldFormat,
  useFormatFieldValue,
} from "@coworker/apprestructured/src/task-management/hooks/useFormatFieldValue";
import TaskInfoPanel from "@coworker/apprestructured/src/task-management/components/TaskInfoPanel/TaskInfoPanel";
import taskInfoModalContents, {
  TaskInfoModalContentName,
} from "@coworker/apprestructured/src/task-management/components/TaskInfoPanel/TaskInfoModal/TaskInfoModalContents";
import { getTypedValue } from "@coworker/apprestructured/src/shared/helpers/schemaValidator";
import LoadingCreatorFragment from "@coworker/apprestructured/src/task-management/components/LoadingCreatorFragment/LoadingCreatorFragment";

export type TaskInfoPanelCreatorFragmentConfigProps = {
  // Translation key for the label
  label: string;
  // Field path in task data for the sublabel
  sublabelField?: string;
  // Optional: format the sublabel using a named formatter
  sublabelFormat?: FieldFormat;
  // Field path in task data for the numeric value
  valueField: string;
  // Field path in task data for the caption
  captionField?: string;
  // Optional: format the caption using a named formatter
  captionFormat?: FieldFormat;
  // Resolve modal content by name from the registry.
  contentName?: TaskInfoModalContentName;
};

type TaskInfoPanelCreatorFragmentProps = {
  configProps: TaskInfoPanelCreatorFragmentConfigProps;
  commonProps: CreatorFragmentCommonProps;
  ref?: Ref<CreatorFragmentHandle>;
};

function TaskInfoPanelCreatorFragment({
  configProps,
  commonProps,
  ref,
}: TaskInfoPanelCreatorFragmentProps) {
  const {
    label,
    sublabelField,
    sublabelFormat,
    valueField,
    captionField,
    captionFormat,
    contentName,
  } = configProps;
  const { task } = commonProps;

  const value = getTypedValue<number>(getByPath(task, valueField));
  const rawSublabel = sublabelField
    ? getTypedValue<string>(getByPath(task, sublabelField))
    : undefined;
  const rawCaption = captionField
    ? getTypedValue<string>(getByPath(task, captionField))
    : undefined;

  const { t } = useTranslation();
  const formatFieldValue = useFormatFieldValue();

  const sublabel =
    rawSublabel && !isLoading(rawSublabel)
      ? formatFieldValue(rawSublabel, sublabelFormat)
      : "";
  const caption =
    rawCaption && !isLoading(rawCaption)
      ? formatFieldValue(rawCaption, captionFormat)
      : "";

  useImperativeHandle(ref, () => ({
    valid: true,
  }));

  if (isLoading(value)) {
    return <LoadingCreatorFragment />;
  }

  if (contentName) {
    const ModalContent = taskInfoModalContents[contentName];
    if (!ModalContent) return null;
    return (
      <TaskInfoPanel
        label={t(label)}
        sublabel={sublabel}
        value={value}
        caption={caption}
        infoButtonAriaLabel={t(ModalContent.buttonAriaLabelKey)}
        infoContent={({ visible, onClose }) => (
          <ModalContent visible={visible} onClose={onClose} />
        )}
      />
    );
  }

  return (
    <TaskInfoPanel
      label={t(label)}
      sublabel={sublabel}
      value={value}
      caption={caption}
    />
  );
}

export default TaskInfoPanelCreatorFragment;
